/** 비교 헤더 컴포넌트 - Figma 디자인 반영 */

import { FileInfo } from "@/app/ixd-dsd-compare/types";
import { useCompareUrlState } from "@/hooks/ixd-dsd-compare/use-compare-url-state";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import queries from "@/queries";
import { NumberBadge } from "@/toggle-ui-kit/badge";
import { CompareHeaderToolbar } from "./compare-header-toolbar";
import type { MemberAxisOption } from "./region-review-toggle";

interface CompareHeaderProps {
  dsdFile: FileInfo | null;
  ixdFile: FileInfo | null;

  // 편집 관련 (모두 부모가 useIxdEditSession에서 끌어와 주입)
  /** 편집 가능 capability (ixdReportId 존재). false면 토글/액션 모두 비노출 또는 disabled */
  canEdit: boolean;
  /** canEdit 이 false 인 이유. 사용자가 이유를 알 곳이 편집 버튼 tooltip 뿐이다. */
  editDisabledReason: string;
  isEditMode: boolean;
  onEditModeChange: (value: boolean) => void;
  hasUnsavedChanges: boolean;
  /** 현재 보이는 IXD chunk(=표)에 속한 미저장 변경 개수 */
  unsavedCountActiveChunk: number;
  /** 양 fsType 합산 미저장 변경 개수 */
  unsavedCountTotal: number;
  showRawValueInEdit: boolean;
  onToggleRawValue: () => void;
  onResetAllChanges: () => void;
  onSave: () => void;
  onDownload: () => void;
  isActionLocked: boolean;
  saveInFlight: boolean;
  downloadInFlight: boolean;
  isReflecting: boolean;

  // HTML 다운로드
  onDownloadHtml?: () => void;
  isDownloadingHtml?: boolean;

  /** 배정 검토 모드의 member 축 선택 — 툴바가 고르고 DSD 렌더러가 그 축만 싣는다. */
  memberAxes?: MemberAxisOption[];
  reviewAxisId?: string | null;
  onReviewAxisChange?: (axisId: string | null) => void;
  /** 도구줄의 검토 도구 자리(값 위치 검토 켜기 버튼) */
  reviewToolSlot?: ReactNode;
}

export const CompareHeader = ({
  dsdFile,
  ixdFile,
  canEdit,
  editDisabledReason,
  isEditMode,
  onEditModeChange,
  hasUnsavedChanges,
  unsavedCountActiveChunk,
  unsavedCountTotal,
  showRawValueInEdit,
  onToggleRawValue,
  onResetAllChanges,
  onSave,
  onDownload,
  isActionLocked,
  saveInFlight,
  downloadInFlight,
  isReflecting,
  onDownloadHtml,
  isDownloadingHtml,
  memberAxes,
  reviewAxisId,
  onReviewAxisChange,
  reviewToolSlot,
}: CompareHeaderProps) => {
  const { comparisonId } = useCompareUrlState();

  const { data: comparisonSummary } = useQuery({
    ...queries.comparison(comparisonId ?? ""),
    enabled: !!comparisonId,
    staleTime: 10 * 60 * 1000,
  });

  return (
    <header className="flex flex-col shrink-0">
      <CompareHeaderToolbar
        memberAxes={memberAxes}
        reviewAxisId={reviewAxisId}
        onReviewAxisChange={onReviewAxisChange}
        canEdit={canEdit}
        editDisabledReason={editDisabledReason}
        isEditMode={isEditMode}
        onEditModeChange={onEditModeChange}
        hasUnsavedChanges={hasUnsavedChanges}
        unsavedCountActiveChunk={unsavedCountActiveChunk}
        unsavedCountTotal={unsavedCountTotal}
        showRawValueInEdit={showRawValueInEdit}
        onToggleRawValue={onToggleRawValue}
        onResetAllChanges={onResetAllChanges}
        onSave={onSave}
        onDownload={onDownload}
        isActionLocked={isActionLocked}
        saveInFlight={saveInFlight}
        downloadInFlight={downloadInFlight}
        isReflecting={isReflecting}
        onDownloadHtml={onDownloadHtml}
        isDownloadingHtml={isDownloadingHtml}
        reviewToolSlot={reviewToolSlot}
      />

      {/* File Info Bar - Figma: 밝은 회색 배경, 두 개의 흰색 카드 */}
      <div className="flex flex-row h-13 w-full justify-center items-center p-2 gap-6 border-b border-line-normal">
        <div className="flex flex-1 justify-center gap-1">
          <NumberBadge className="bg-label-normal text-static-white box-border p-1.5 size-5 caption-2-medium">
            1
          </NumberBadge>
          <div className="flex justify-center items-center label-2-semibold">
            {dsdFile ? dsdFile.name : comparisonSummary?.dsdFilename}
          </div>
        </div>
        <div className="flex flex-1 justify-center gap-1">
          <NumberBadge className="bg-label-normal text-static-white box-border p-1.5 size-5 caption-2-medium">
            2
          </NumberBadge>
          <div className="flex justify-center items-center label-2-semibold">
            {ixdFile ? ixdFile.name : comparisonSummary?.ixdFilename}
          </div>
        </div>
      </div>
    </header>
  );
};
